import { AddressSuggestion } from "@/components/forms/types";
import { POINTS_INTERET } from "@/components/forms/constants";

// Cache pour les résultats de recherche d'adresses
const addressCache: Record<
  string,
  { data: AddressSuggestion[]; timestamp: number }
> = {};
const CACHE_DURATION = 1000 * 60 * 60; // 1 heure

export const searchAddresses = async (
  query: string,
  includePOI: boolean = true,
): Promise<AddressSuggestion[]> => {
  try {
    // Vérifier si les résultats sont dans le cache
    const cacheKey = `${query}-${includePOI}`;
    const cachedResult = addressCache[cacheKey];

    if (cachedResult && Date.now() - cachedResult.timestamp < CACHE_DURATION) {
      return cachedResult.data;
    }

    let allSuggestions: AddressSuggestion[] = [];

    // Recherche dans les points d'intérêt si demandé
    if (includePOI) {
      const queryLower = query.toLowerCase();
      const poiResults = [
        ...POINTS_INTERET.gares,
        ...POINTS_INTERET.aeroports,
        ...POINTS_INTERET.hotels,
      ]
        .filter((poi) => {
          const matchName = poi.nom.toLowerCase().includes(queryLower);
          const matchAddress = poi.adresse.toLowerCase().includes(queryLower);
          const matchType =
            (queryLower.includes("gare") && poi.type === "gare") ||
            ((queryLower.includes("aero") || queryLower.includes("aéro")) &&
              poi.type === "aeroport") ||
            ((queryLower.includes("hotel") || queryLower.includes("hôtel")) &&
              poi.type === "hotel");
          return matchName || matchAddress || matchType;
        })
        .map((poi) => ({
          id: poi.id,
          label: poi.adresse,
          name: poi.nom,
          type: poi.type,
          icon: poi.icon,
          postcode: poi.adresse.match(/\d{5}/)?.[0] || "",
          city: poi.adresse.split(",")[1]?.trim() || "",
          isPOI: true,
          context:
            poi.type === "gare"
              ? "(Gare)"
              : poi.type === "aeroport"
                ? "(Aéroport)"
                : poi.type === "hotel"
                  ? "(Hôtel)"
                  : "(POI)",
        }));

      allSuggestions = [...poiResults];
    }

    // Recherche via l'API gouvernementale
    const response = await fetch(
      `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(
        query,
      )}&limit=5`,
    );

    if (!response.ok) {
      throw new Error("Erreur lors de la recherche d'adresses");
    }

    const data = await response.json();

    if (data.features) {
      const apiSuggestions = data.features.map((feature: any) => ({
        id: feature.properties.id || String(Math.random()),
        label: feature.properties.label,
        name: feature.properties.name || feature.properties.label.split(",")[0],
        postcode: feature.properties.postcode,
        city: feature.properties.city,
        type: feature.properties.type,
        context: feature.properties.context,
        isPOI: false,
        score: feature.properties.score,
      }));

      allSuggestions = [...allSuggestions, ...apiSuggestions];
    }

    // Mettre en cache les résultats
    addressCache[cacheKey] = {
      data: allSuggestions,
      timestamp: Date.now(),
    };

    return allSuggestions;
  } catch (error) {
    console.error("Erreur lors de la recherche d'adresses:", error);
    return [];
  }
};

// Cache pour les coordonnées
const coordinatesCache: Record<
  string,
  { data: { lat: number; lon: number } | null; timestamp: number }
> = {};

export const getCoordinates = async (
  address: string,
): Promise<{ lat: number; lon: number } | null> => {
  try {
    // Vérifier si les coordonnées sont dans le cache
    const cachedResult = coordinatesCache[address];

    if (cachedResult && Date.now() - cachedResult.timestamp < CACHE_DURATION) {
      return cachedResult.data;
    }

    const response = await fetch(
      `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(address)}&limit=1`,
    );
    const data = await response.json();

    if (data.features && data.features[0]) {
      const [lon, lat] = data.features[0].geometry.coordinates;
      const result = { lat, lon };

      // Mettre en cache les résultats
      coordinatesCache[address] = {
        data: result,
        timestamp: Date.now(),
      };

      return result;
    }
    return null;
  } catch (error) {
    console.error("Erreur lors de la récupération des coordonnées:", error);
    return null;
  }
};

// Cache pour les distances
const distanceCache: Record<string, { distance: number; timestamp: number }> =
  {};

export const getRouteDistance = async (
  start: { lon: number; lat: number },
  end: { lon: number; lat: number },
): Promise<number | null> => {
  try {
    // Créer une clé de cache unique pour ce trajet
    const cacheKey = `${start.lon},${start.lat}-${end.lon},${end.lat}`;

    // Vérifier le cache
    const cachedResult = distanceCache[cacheKey];
    if (cachedResult && Date.now() - cachedResult.timestamp < CACHE_DURATION) {
      return cachedResult.distance;
    }

    const response = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${start.lon},${start.lat};${end.lon},${end.lat}?overview=false`,
    );
    const data = await response.json();

    if (data.routes && data.routes[0]) {
      const correctionCoefficient = 1.15;
      const distance = (data.routes[0].distance / 1000) * correctionCoefficient;

      // Mettre en cache le résultat
      distanceCache[cacheKey] = {
        distance,
        timestamp: Date.now(),
      };

      return distance;
    }
    return null;
  } catch (error) {
    console.error("Erreur lors du calcul de la distance routière:", error);
    return null;
  }
};

export const calculateDirectDistance = async (
  startAddress: string,
  endAddress: string,
): Promise<number | null> => {
  try {
    // Créer une clé de cache unique pour ce trajet
    const cacheKey = `${startAddress}-${endAddress}`;

    // Vérifier le cache
    const cachedResult = distanceCache[cacheKey];
    if (cachedResult && Date.now() - cachedResult.timestamp < CACHE_DURATION) {
      return cachedResult.distance;
    }

    // Obtenir les coordonnées en parallèle
    const [startResponse, endResponse] = await Promise.all([
      fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(startAddress)}&limit=1`,
      ),
      fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(endAddress)}&limit=1`,
      ),
    ]);

    const [startData, endData] = await Promise.all([
      startResponse.json(),
      endResponse.json(),
    ]);

    if (!startData.features?.[0] || !endData.features?.[0]) {
      return null;
    }

    const [startLon, startLat] = startData.features[0].geometry.coordinates;
    const [endLon, endLat] = endData.features[0].geometry.coordinates;

    // Calculer la distance à vol d'oiseau
    const R = 6371; // Rayon de la Terre en km
    const dLat = ((endLat - startLat) * Math.PI) / 180;
    const dLon = ((endLon - startLon) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((startLat * Math.PI) / 180) *
        Math.cos((endLat * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceVolDOiseau = R * c;

    // Ajuster le coefficient en fonction de la distance
    let routeCoefficient;
    if (distanceVolDOiseau <= 20) {
      // Pour les courtes distances (<20km), on garde un coefficient plus élevé
      routeCoefficient = 1.4;
    } else if (distanceVolDOiseau <= 50) {
      // Pour les distances moyennes (20-50km), on réduit progressivement
      routeCoefficient = 1.3;
    } else {
      // Pour les longues distances (>50km), on utilise un coefficient plus faible
      routeCoefficient = 1;
    }

    const estimatedRouteDistance = distanceVolDOiseau * routeCoefficient;

    // Mettre en cache le résultat
    distanceCache[cacheKey] = {
      distance: estimatedRouteDistance,
      timestamp: Date.now(),
    };

    return estimatedRouteDistance;
  } catch (error) {
    console.error("Erreur lors du calcul de la distance:", error);
    return null;
  }
};
